import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {map} from "lit/directives/map.js";
import {when} from "lit/directives/when.js";
import {DateTime} from "luxon";
import {Issue} from "../../domain/entities/issue.entity";
import {DateHelper} from "../../helpers/date.helper";
import {StringHelper} from "../../helpers/string.helper";
import {AbstractLitElement} from "../abstract-lit.element";
import {translate} from "lit-translate";
import {repeat} from "lit/directives/repeat.js";
import {until} from "lit/directives/until.js";

@customElement(`ha-issue-preview`)
export class HaIssuePreviewComponent extends AbstractLitElement {

    @property({type: Object})
    public issue!: Issue;

    protected render(): unknown {

        return html`
            <main class="issue-preview">
                ${when(this.issue.title.length > 0, () => html`
                    <div class="preview-title">
                        ${this.issue.title}
                    </div>
                `)}
                <div class="ha-combobox-readonly preview-creator  " data-type="creator"
                     label="Ersteller">
                    <label>${translate('general.creator')}</label>
                    <div class="wrapper">
                        <div class="selected">
                            <span class="label">
                                <span class="title">
                                    ${this.issue.creator?.name ?? html`-`}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="ha-combobox-multi-readonly   preview-assignment" data-type="assignee"
                     label="Bearbeiter">
                    <label>${translate('general.assignee')}</label>
                    <div class="wrapper">
                        <div class="selected">${when(this.issue.employeeToIssueAssignments.length,
                                () => html`
                                    ${repeat(this.issue.employeeToIssueAssignments, e => e.uid, assignment => html`
                                        ${until(assignment.employee!.then(employee => html`
                                            <span class="label">
                                                <span class="title">${employee.name}</span>
                                                <span class="timeBudget">${DateHelper.convertDurationToString(assignment.timeBudget)}</span>
                                            </span>
                                        `), `-`)}
                                    `)}
                                `,
                                () => html`
                                    <span class="label"><span class="title">-</span></span>
                                `
                        )}
                        </div>
                    </div>
                </div>
                <div class="ha-combobox-readonly   preview-date-range" data-type="timeslot">
                    <label>${translate('components.ha-dialog.timeslot')}</label>
                    <div class="wrapper">
                        <div class="selected">
                            <span class="label">
                                <span class="title">
                                    ${this._formatDateRange(this.issue.start, this.issue.end)}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="ha-combobox-readonly   preview-type" data-type="type" label="Aufgabentyp">
                    <label>${translate('general.type')}</label>
                    <div class="wrapper">
                        <div class="selected">
                            <span class="label">
                                <span class="title">
                                ${this.issue.type?.toString() ?? `-`}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="ha-combobox-readonly   preview-board" data-type="board" label="Board">
                    <label>${translate('general.boards')}</label>
                    <div class="wrapper">
                        <div class="selected">
                            <span class="label">
                                <span class="title">
                                    ${this.issue.project?.title ?? `-`}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="ha-combobox-multi-readonly   preview-label" data-type="label"
                     label="Labels">
                    <label>${translate('general.label')}</label>
                    <div class="wrapper">
                        <div class="selected">${when(this.issue.keywords.length,
                                () => html`
                                    ${map(this.issue.keywords, keyword => html`
                                        <span class="label">
                                            <span class="title">${keyword.key}</span>
                                        </span>
                                    `)}
                                `,
                                () => html`
                                    <span class="label"><span class="title">-</span></span>
                                `
                        )}
                        </div>
                    </div>
                </div>
                ${when(
                        StringHelper.hasQuillContent(this.issue.description),
                        () => html`
                            <div class="preview-description" data-type="description">
                                <label>${translate('components.ha-dialog.description')}</label>
                                <ha-quill-viewer .value="${this.issue!.description}"></ha-quill-viewer>
                            </div>
                        `)}
            </main>
        `;
    }

    private _formatDateRange(start: DateTime, end: DateTime): HTMLTemplateResult {
        end = end.minus({day: 1});

        const diff = end.diff(start).as(`days`);
        return html`
            ${DateHelper.fromDateTimeToDateString(start)}
            ${when(diff > 0, () => html`&ndash; ${DateHelper.fromDateTimeToDateString(end)}`)}
        `
    }
}